<script setup lang="ts">
import { ref, computed, onMounted, h, reactive, onUnmounted } from 'vue';
import { getCurrentLocale } from '@/i18n';
import { useI18n } from 'vue-i18n';
import { DateTime } from 'luxon';
import { useRouter } from 'vue-router';
import routerKey from '@/router/routerKey';
import { useDebounceFn } from '@vueuse/core';
import cloneDeep from 'lodash/cloneDeep';
import { getUsersByDept } from '@/utils';
import { getEnvVar } from '@/utils/env';
import { useLayout } from '@/layout/composables/layout';
import { useViewComposable } from '@/views/composable';
import { formatDate } from '@/utils/dateUtils';
import { replaceAll } from '@/utils/stringUtils';
import { useClientCopyComposable } from '@/components/modal/clientCopy/useClientCopyComposable';
import { UserInfo } from '@/modules/user/UserInfo';
import { useUserInfo } from '@/modules/user/useUserInfo';
import { useAirManagementStore } from '@/store/AirManagementStore';
import { UserStore } from '@/store/UserStore';
import { useGroupTypeStore } from '@/store/GroupTypeStore';
import { useAdminQuoteStore } from '@/store/AdminQuoteStore';
import { useQuoteAssignStore } from '@/store/QuoteAssignStore';
import { useMyQuoteStore } from '@/store/MyQuoteStore';
import { useUserAuthorizationsStore } from '@/store/UserAuthorizationsStore';
import { useBookingQuoteStore } from '@/store/BookingQuoteStore';
import { useViewQuoteStore } from '@/store/ViewQuoteStore';
import { useViewRuleStore } from '@/store/ViewRuleStore';
import {
  AIR_ONLY,
  CRUISE,
  HOTEL_ONLY,
  PACKAGE,
  TOUR,
  QT_COORDINATOR,
  QT_SUPERVISOR,
  QT_AIR,
  QT_CRUISE,
  QT_INCENTIVE,
  QT_COMMERCIAL,
  QT_REVENUE,
  MAP_DEPARTMENT_TO_ROLE,
  GQT_SUPERVISOR,
  GQT_COORDINATOR,
  GQT_COMMERCIAL,
  GQT_AIR,
  GQT_CRUISE,
  GQT_INCENTIVE,
  GQT_REVENUE,
  GQT_ADMIN,
  UNASSIGNED,
  ASSIGNED_TO,
  QUOTE_STS,
  DEPT_STS,
  ASSIGNEE,
  RQ_TO_BOOK,
  DEPT,
  NEW,
  RM,
  IN_PROGRESS,
  PRICE_MATCH,
  QUOTE_READY,
  COMPLETED,
  MAP_ROLE_TO_DEPARTMENT,
  EXPIRED,
  LANG_FRENCH,
  DEPOSIT_DUE,
  FINAL_PAYMENT_DUE,
  COMPLETE_NAMES,
  EN_FORMAT_CURRENCY,
  FR_FORMAT_CURRENCY,
  CREATED,
  INCENTIVE_GROUP_TYPE,
  GQT_SUPPORT
} from '@/constants';
import { FilterMatchMode, FilterOperator } from '@primevue/core/api';
import { useDialog } from 'primevue/usedialog';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import DatePicker from 'primevue/datepicker';
import InputText from 'primevue/inputtext';
import TableColumn from 'primevue/column';
import Select from 'primevue/select';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import AdminStats from '@/components/stats/AdminStats.vue';
import BookFormManagement from '@/components/BookFormManagement.vue';
import ConfirmUpdatePDFModal from '@/components/modal/ConfirmUpdatePDFModal.vue';
import CruiseManualModal from '@/components/modal/worksheet/CruiseManualModal.vue';
import NoticeNotFoundRuleTourInfo from '@/components/modal/NoticeNotFoundRuleTourInfo.vue';
import FileNotes from '@/components/notes/FileNotes.vue';
import FilterTable from '@/components/common/FilterTable.vue';
import FlightManualModal from '@/components/modal/worksheet/FlightManualModal.vue';
import HotelQuoteModal from '@/components/modal/worksheet/HotelQuoteModal.vue';
import PackageInstaQuoteModal from '@/components/modal/worksheet/PackageInstaQuoteModal.vue';
import Payment from '@/components/modal/Payment.vue';
import SeeMoreToursDescription from '@/components/modal/tour/SeeMoreToursDescription.vue';
import TaskList from '@/components/notes/TaskList.vue';
import TourQuoteModal from '@/components/modal/worksheet/TourQuoteModal.vue';
import TourViewRuleModal from '@/components/modal/TourViewRuleModal.vue';
import UrlQuoteModal from '@/components/modal/UrlQuoteModal.vue';
import ValidateQuoteFormNotification from '@/components/modal/ValidateQuoteFormNotification.vue';
import ViewRuleModal from '@/components/modal/ViewRuleModal.vue';
import ViewPDFModal from '@/components/modal/ViewPDFModal.vue';

const BASE_URL = getEnvVar('VITE_CDN_BASE_URL');
const REFRESH_RATE = getEnvVar('VITE_REFRESH_QUOTE_RATE');
const loadingIcon = `${BASE_URL}/assets/images/loading.png`;
const { t } = useI18n({ useScope: 'global' });
const dialog = useDialog();
const toast = useToast();
const userAuthorizationsStore = useUserAuthorizationsStore();
const authorizations: any = userAuthorizationsStore.permissionByRole;
const currentLanguage = getCurrentLocale();
const userInfo = useUserInfo();
const currentUser = ref<UserInfo>();
const quotes = ref<any>([]);
const filters = ref();
const loading = ref(false);
const runningLazyLoad = ref(0);
const pageSizes = ref([10, 25, 50, 100, 200, 400, 500]);
const startElement = ref(0);
const dataTableQuoteRef = ref();
const filterGlobalField = ref('');
const totalRecords = ref(null);
const groupTypeList = ref([]);
const isLoading = ref(false);
const isNotExisted = ref(false);
const departs = ref([QT_COORDINATOR, QT_SUPERVISOR, QT_AIR, QT_CRUISE, QT_INCENTIVE, QT_COMMERCIAL, QT_REVENUE]);
const minDateRangeDefault = DateTime.utc().minus({ days: 60 }).toJSDate();
const maxDateRangeDefault = new Date();
const deptChangedName = ref('default');
const isReloading = ref(false);
const statisticNoteTotalPublic = ref<any>();
const userRolePublic = ref<any>('');
const filterTotalData = ref<any>();

const { formatCurrency } = useClientCopyComposable(currentLanguage, currentLanguage === 'en' ? EN_FORMAT_CURRENCY : FR_FORMAT_CURRENCY);
const { quoteSts, priorityStatus, statusPriorityNumber, departments, renderDateTimeOption, getDepartment, renderLocalTimeFromUtc, createUnassignedStatus } = useViewComposable();

const deptSts = ref([
  { label: t('status.new').toUpperCase(), hide: false, value: NEW },
  { label: t('quote_status.in_progress').toUpperCase(), hide: false, value: IN_PROGRESS },
  { label: t('quote_status.completed').toUpperCase(), hide: false, value: COMPLETED }
]);

const langQuote = ref([
  {
    label: t('promos.labels.english'),
    value: t('promos.labels.english')
  },
  {
    label: t('promos.labels.french'),
    value: t('promos.labels.french')
  }
]);

const initValue = () => {
  return {
    typeOfSearch: CREATED,
    fromDate: minDateRangeDefault,
    toDate: maxDateRangeDefault,
    quoteType: [PACKAGE, HOTEL_ONLY, AIR_ONLY, CRUISE, TOUR],
    quoteId: '',
    groupType: [],
    showArchivedOnly: false
  };
};

const initFilters = () => {
  filters.value = {
    global: { value: filterGlobalField.value, matchMode: FilterMatchMode.CONTAINS },
    quoteID: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
    language: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
    quoteSts: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
    assignedTo: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
    groupName: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
    agencyPhone: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
    groupType: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
    hotelDestinations: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
    depDateOption: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
    expires: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }] },
    dept: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
    deptSts: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] }
  };
};
initFilters();

const initValueFilter = reactive(initValue());
const { layoutState } = useLayout();
const adminQuoteStore = useAdminQuoteStore();
const viewQuoteStore = useViewQuoteStore();
const viewRuleStore = useViewRuleStore();
const quoteAssignStore = useQuoteAssignStore();
const userStore = UserStore();
const myQuoteStore = useMyQuoteStore();
const bookingQuoteStore = useBookingQuoteStore();
const airManagementStore = useAirManagementStore();
const groupTypeManagement = useGroupTypeStore();
const router = useRouter();

const doLoading = computed(() => quoteAssignStore.doLoading);
const listUser = computed(() => userStore.selectUserAssign);

const getRole = async () => {
  const currentUser = await userInfo.getUserInfo();
  const userRole = currentUser?.role?.toUpperCase();
  if (MAP_ROLE_TO_DEPARTMENT[userRole]) {
    userRolePublic.value = MAP_ROLE_TO_DEPARTMENT[userRole];
  } else {
    userRolePublic.value = userRole;
  }
};

getRole();

const getGroupTypeData = async () => {
  await groupTypeManagement.getGroupTypeList().then((response) => {
    groupTypeList.value = response.data;
    initValueFilter.groupType = getGroupTypeIDs(response.data);
  });
};

const handleFilterByRole = (userRole: string) => {
  switch (userRole) {
    case GQT_AIR:
      filters.value.dept.constraints[0].value = QT_AIR;
      break;
    case GQT_CRUISE:
      filters.value.dept.constraints[0].value = QT_CRUISE;
      break;
    case GQT_COMMERCIAL:
      filters.value.dept.constraints[0].value = QT_COMMERCIAL;
      break;
    case GQT_INCENTIVE:
      filters.value.dept.constraints[0].value = QT_INCENTIVE;
      break;
    case GQT_REVENUE:
      filters.value.dept.constraints[0].value = QT_REVENUE;
      break;
    default:
      filters.value.dept.constraints[0].value = null;
      break;
  }
};

const getGroupTypeIDs = (groupTypes) => {
  return groupTypes.map((item) => item?.sk);
};

const handleFilterData = (dataFilter) => {
  quotes.value = [];
  startElement.value = 0;
  if (dataFilter) {
    initValueFilter.typeOfSearch = dataFilter.typeOfSearch;
    initValueFilter.fromDate = dataFilter.fromDate || initValue().fromDate;
    initValueFilter.toDate = dataFilter.toDate || initValue().toDate;
    initValueFilter.quoteType = dataFilter.quoteType;
    initValueFilter.groupType = dataFilter.groupType;
    initValueFilter.quoteId = dataFilter.quoteId;
  } else {
    initValueFilter.typeOfSearch = initValue().typeOfSearch;
    initValueFilter.fromDate = initValue().fromDate;
    initValueFilter.toDate = initValue().toDate;
    initValueFilter.quoteType = initValue().quoteType;
    initValueFilter.groupType = getGroupTypeIDs(groupTypeList.value);
    initValueFilter.quoteId = initValue().quoteId;
  }
  getListQuote();
};

const hasUnreadNotes = (notes: any) => {
  return notes.some((note: any) => note.departments.some((department: any) => department.department === userRolePublic.value && !department.isNoteRead));
};

const calculateStatistics = (quotes: any) => {
  const statisticNoteTotal: any = {
    TOTAL: { count: 0, quoteIds: [] },
    UNREAD: { count: 0, quoteIds: [] },
    ACKNOWLEDGE: { count: 0, quoteIds: [] }
  };

  quotes.forEach((quote: any) => {
    Object.keys(statisticNoteTotal).forEach((key) => {
      if (quote.statisticNote && quote.statisticNote[key]) {
        statisticNoteTotal[key].count += quote.statisticNote[key].count;
        const quoteIdsArray = quote.statisticNote[key].quoteIds.split(',').filter((id: string) => id);
        statisticNoteTotal[key].quoteIds = Array.from(new Set([...statisticNoteTotal[key].quoteIds, ...quoteIdsArray]));
        quote.hasNoteToRead = hasUnreadNotes(quote.notes);
      }
    });
  });
  statisticNoteTotalPublic.value = statisticNoteTotal;
};

async function fetchListQuoteInRange(fromDate: number, toDate: number) {
  const payload = {
    ...initValueFilter,
    fromDate: new Date(fromDate).toISOString(),
    toDate: new Date(toDate).toISOString()
  };

  function insertSorted(arr, val, comparator) {
    let left = 0;
    let right = arr.length;
    while (left < right) {
      const mid = Math.floor((left + right) / 2);
      if (comparator(arr[mid], val) <= 0) {
        left = mid + 1;
      } else {
        right = mid;
      }
    }
    arr.splice(left, 0, val);
    return arr;
  }

  await adminQuoteStore
    .getListQuotes(MAP_ROLE_TO_DEPARTMENT[currentUser?.value?.role], payload)
    .then((res) => {
      if (!res) {
        return;
      }
      res?.forEach((data) => {
        data.internalSts = data?.internalSts?.replace('_', ' ');
        data.assignedTo = data.assignedTo === UNASSIGNED ? t('status.unassigned') : data.assignedTo;
        data.groupTypePaxRoom = converArrayToString(data.gTypePaxMyQuote, '-');
        data.hotelDestinations = converArrayToString(data.hotelDestinations, '-');
        data.depDateOption = renderDateTimeOption(data.depDateOption);
        data.created = renderLocalTimeFromUtc(data.created);
        data.expires = renderLocalTimeFromUtc(data.expires);
        data.language = renderLanguage(data.language);
        data.groupType = currentLanguage === LANG_FRENCH ? data.groupTypeFr : data.groupType;
        data.linkedQuotes = convertString(data.linkedQuotes);
      });
      res.forEach((item: any) => {
        const objIndex = quotes.value.findIndex((q) => q.quoteID == item.quoteID);
        if (objIndex > -1) {
          quotes.value[objIndex] = item;
        } else {
          insertSorted(quotes.value, item, (a, b) => a.created.getTime() < b.created.getTime());
        }
      });
      calculateStatistics(quotes.value);
      totalRecords.value = totalRecords.value + res.length;
    })
    .catch(() => {
      toast.add({
        severity: 'info',
        summary: t('quote_management.toastMessage.fetch_quote.failed_summary'),
        detail: t('quote_management.toastMessage.fetch_quote.failed_detail').replace('%fromDate%', renderDateTimeOption(fromDate)).replace('%toDate%', renderDateTimeOption(toDate)),
        life: 3000
      });
    });
}

const getListQuote = async (shouldLock: boolean = true) => {
  const startDate = new Date(initValueFilter.fromDate).setHours(0, 0, 0, 0);
  const endDate = new Date(initValueFilter.toDate).setHours(23, 59, 59, 0);
  const intervalInDays = 5;
  await fetchDataInRange(startDate, endDate, intervalInDays, shouldLock);
};

const handleClearFilter = (filterCallback: any) => {
  filterCallback();
};

const handleFilterApply = (filterCallback: any) => {
  filterCallback();
};

const fetchDataInRange = async (startDate: any, endDate: any, intervalInDays: number, shouldLock: boolean): Promise<void> => {
  const start = new Date(startDate);
  const currentDate = new Date(endDate);

  if (shouldLock) {
    loading.value = true;
  }
  totalRecords.value = null;
  runningLazyLoad.value = runningLazyLoad.value + 1;
  const localRunningLazyLoad = runningLazyLoad.value;
  while (currentDate >= start) {
    let fromDate = new Date(currentDate).setHours(0, 0, 0, 0);
    const toDate = new Date(currentDate).setHours(23, 59, 59, 0);
    fromDate = fromDate - intervalInDays * 24 * 60 * 60 * 1000;
    if (fromDate < startDate) {
      fromDate = startDate;
    }
    try {
      if (localRunningLazyLoad === runningLazyLoad.value) {
        await fetchListQuoteInRange(fromDate, toDate);
      }
    } finally {
      loading.value = false;
    }
    currentDate.setDate(currentDate.getDate() - intervalInDays - 1);
  }
};

const handleRequote = async (quoteType: string, id: string) => {
  isLoading.value = true;
  await viewQuoteStore.handleReQuoteWorkSheet(id).finally(() => {
    isLoading.value = false;
  });

  if (quoteType === PACKAGE) {
    viewQuoteStore.getAction('requote');
    viewQuoteStore.getActionRequoteFromWorksheet();
    router.push({ name: routerKey.HOME });
  } else if (quoteType === HOTEL_ONLY) {
    viewQuoteStore.getAction('requote');
    viewQuoteStore.getActionRequoteFromWorksheet();
    router.push({ name: routerKey.HOTEL });
  } else if (quoteType === AIR_ONLY) {
    viewQuoteStore.getAction('requote');
    viewQuoteStore.getActionRequoteFromWorksheet();
    router.push({ name: routerKey.AIR });
  } else if (quoteType === CRUISE) {
    viewQuoteStore.getAction('requote');
    viewQuoteStore.getActionRequoteFromWorksheet();
    router.push({ name: routerKey.CRUISE });
  } else {
    viewQuoteStore.getAction('requote');
    viewQuoteStore.getActionRequoteFromWorksheet();
    router.push({ name: routerKey.MULTI_TOURS });
  }
};

const openWorksheetModal = async (quoteType, id, langQuote) => {
  isLoading.value = true;
  await adminQuoteStore.getQuoteDetail(id).finally(() => {
    isLoading.value = false;
  });

  let Component;
  switch (quoteType) {
    case PACKAGE:
      Component = PackageInstaQuoteModal;
      break;
    case HOTEL_ONLY:
      Component = HotelQuoteModal;
      break;
    case AIR_ONLY:
      Component = FlightManualModal;
      break;
    case CRUISE:
      Component = CruiseManualModal;
      break;
    case TOUR:
      Component = TourQuoteModal;
      break;
    default:
      break;
  }
  const dialogRef = dialog.open(Component, {
    props: {
      style: { width: '90vw' },
      modal: true,
      maximizable: true
    },
    templates: {
      header: () => {
        return [
          h(
            'div',
            { class: 'flex align-items-center w-full justify-content-between pr-4' },
            [h('span', { class: 'text-xl font-bold', innerHTML: `${t('worksheet.worksheet')} (${id})  (${langQuote})` })],
            [
              authorizations?.quote_management?.replicate_quote_view &&
                h(Button, {
                  label: t('worksheet.replicate_quote'),
                  onClick: async () => {
                    await handleRequote(quoteType, id);
                    dialogRef.close();
                  }
                })
            ]
          )
        ];
      }
    },
    emits: {
      reloadList: async () => {
        await getListQuote();
      }
    }
  });
};

const clearFilter = async () => {
  filterTotalData.value = undefined;
  filterGlobalField.value = '';
  initFilters();
  dataTableQuoteRef.value.d_sortField = '0';
  await getListQuote();
};

const refreshPage = async () => {
  isReloading.value = true;
  getListQuote().finally(() => {
    isReloading.value = false;
  });
};

const openExtNotes = async (qId: string, isRead: boolean, data: any) => {
  isLoading.value = true;
  await adminQuoteStore.getNote(qId).finally(() => {
    isLoading.value = false;
  });

  dialog.open(FileNotes, {
    data: {
      page: 'QUOTEADMIN',
      quoteId: qId,
      service: adminQuoteStore,
      isRead: isRead,
      isMyQuote: false,
      data: data
    },
    props: {
      header: t('note.file_notes'),
      modal: true,
      maximizable: true,
      style: {
        width: '90vw'
      }
    },
    onClose: async () => {
      if (filterTotalData.value) {
        await handleFilterTotal(filterTotalData.value);
      } else {
        await getListQuote();
      }
    }
  });
};

const staticEvent = (status: string) => {
  if (status !== 'ALL' && [GQT_COORDINATOR, GQT_SUPERVISOR, GQT_COMMERCIAL, GQT_REVENUE, GQT_AIR, GQT_CRUISE, GQT_INCENTIVE, GQT_ADMIN, GQT_SUPPORT].includes(currentUser.value?.role)) {
    filters.value.quoteSts.constraints[0].value = status;
  } else {
    filters.value.quoteSts.constraints[0].value = '';
  }
  if (GQT_COORDINATOR === currentUser.value?.role) {
    filters.value.assignedTo.constraints[0].value = getUserNameByEmail(currentUser.value?.role, currentUser.value?.email, listUser.value);
  } else {
    handleFilterByRole(currentUser.value?.role ?? '');
  }
  document.getElementById('dtManualquote').scrollIntoView({
    behavior: 'smooth',
    block: 'start',
    inline: 'nearest'
  });
};

const openModalUpdatePDF = (data) => {
  const dialogRef = dialog.open(ConfirmUpdatePDFModal, {
    props: {
      modal: true,
      header: t('create_quote.confirm')
    },
    emits: {
      onSave: () => {
        completeQuote(data, true);
        dialogRef.close();
      },
      onCancel: () => {
        dialogRef.close();
      },
      onUpdate: () => {
        completeQuote(data, false);
        dialogRef.close();
      }
    }
  });
};

const completeQuote = (data: any, isSent: boolean) => {
  isLoading.value = true;
  quoteAssignStore
    .handleComplete(data.quoteID, isSent)
    .then(async () => {
      toast.add({ severity: 'success', summary: '', detail: t('quote_management.toastMessage.update_pdf_successfully'), life: 3000 });
      const currentQuote = await adminQuoteStore.getQuoteDetail(data.quoteID);
      if (isSent) {
        await updateQuoteSts(data, QUOTE_READY);
        data.quoteSts = QUOTE_READY;
      }
      data = currentQuote;
    })
    .catch(() => {
      const modalRef = dialog.open(ValidateQuoteFormNotification, {
        data: {
          message: t('notification.update_worksheet_message'),
          title: t('notification.update_worksheet_title'),
          isAlignCenter: true
        },
        props: {
          header: t('common.warning'),
          modal: true
        },
        templates: {
          footer: () => {
            return [h('div', { class: 'text-center' }, h(Button, { autofocus: true, label: t('common.buttons.ok'), onClick: () => modalRef.close() }))];
          }
        }
      });
    })
    .finally(() => {
      isLoading.value = false;
    });
};

const openBookingModal = async (qId) => {
  isLoading.value = true;
  await bookingQuoteStore.getQuoteDetail(qId).finally(() => {
    isLoading.value = false;
  });

  const dialogRef = dialog.open(BookFormManagement, {
    props: {
      header: t('my_quote.rooming_list'),
      modal: true,
      style: {
        width: '90vw'
      }
    },
    data: {
      quoteId: qId,
      isManagement: true
    },
    emits: {
      saveBooking: async (value) => {
        if (value === true) {
          dialogRef.close();
          await getListQuote();
        } else {
          dialogRef.close();
        }
      }
    },
    onClose: () => {
      document.getElementsByTagName('body')[0].classList.remove('p-overflow-hidden-2');
    }
  });
};

const handleAssignRole = (data: any) => {
  if ((data.groupType && data.groupType !== INCENTIVE_GROUP_TYPE) || (data.gType && data.gType !== INCENTIVE_GROUP_TYPE)) {
    if (data.quoteID.includes('IM') || data.quoteID.includes('IQ') || data.quoteID.includes('HM')) {
      return QT_COORDINATOR;
    }
    if (data.quoteID.includes('AM')) {
      return QT_AIR;
    }
    if (data.quoteID.includes('CM')) {
      return QT_CRUISE;
    }
  }
  return QT_INCENTIVE;
};

const updateQuoteSts = async (data: any, event: any) => {
  const formatStatus = replaceAll(event, '_', ' ');
  const payload = {
    assignType: QUOTE_STS,
    value: event,
    email: data.coEmail
  };
  const role = handleAssignRole(data);
  await quoteAssignStore
    .handleAssign(payload, data.quoteID)
    .then(async () => {
      data.hasNoteToRead = true;
      if (event === EXPIRED) {
        data.expires = new Date();
      }
      toast.add({ severity: 'success', summary: '', detail: t('quote_management.toastMessage.update_status', { quoteID: data.quoteID, status: formatStatus }), life: 3000 });
      if (payload.assignType === QUOTE_STS && payload.value === RQ_TO_BOOK) {
        await Promise.all([quoteAssignStore.handleAssign({ assignType: DEPT, value: role, email: data.coEmail }, data.quoteID), updateDeptSts(NEW, data)]);
      }
      if (payload.value === PRICE_MATCH) {
        await quoteAssignStore.handleAssign({ assignType: DEPT, value: RM, email: data.coEmail }, data.quoteID);
        data.dept = RM;
      }
    })
    .catch(() => toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 }));
};

const updateDeptSts = async (event: any, data: any) => {
  const deptSts = event.value ? event.value.replace(' ', '_') : event.replace(' ', '_');
  const payload = {
    assignType: DEPT_STS,
    value: deptSts,
    email: data.coEmail
  };
  quoteAssignStore
    .handleAssign(payload, data.quoteID)
    .then(() => {
      toast.add({ severity: 'success', summary: '', detail: `Quote ${data.quoteID} has been changed dept status to ${deptSts}`, life: 3000 });
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.save_error'), life: 5000 });
    });
};

const handleAssignTo = async (event, data, listUser) => {
  const messAssign = t('quote_management.toastMessage.handleAssignTo').replace('%QuoteID%', data.quoteID).replace('%Value%', event.value);
  const quoteAssigneeEmail = listUser?.find((user) => user.label === event.value)?.email;
  const payload = {
    assignType: ASSIGNED_TO,
    value: event.value,
    email: quoteAssigneeEmail
  };
  quoteAssignStore
    .handleAssign(payload, data.quoteID)
    .then(() => {
      toast.add({ severity: 'success', summary: '', detail: messAssign, life: 3000 });
      data.coEmail = quoteAssigneeEmail;
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.save_error'), life: 5000 });
    });
};

const updateAssignee = async (event, data, listUser) => {
  const messUpdateAss = t('quote_management.toastMessage.updateAssignee').replace('%QuoteID%', data.quoteID).replace('%Value%', event.value);
  toast.add({ severity: 'success', summary: '', detail: messUpdateAss, life: 3000 });
  const payload = {
    assignType: ASSIGNEE,
    value: event.value,
    email: listUser?.find((user) => user.label === event.value)?.email
  };
  await quoteAssignStore.handleAssign(payload, data.quoteID);
};

const updateDept = async (event, data) => {
  const messUpdateDept = t('quote_management.toastMessage.updateDept').replace('%QuoteID%', data.quoteID).replace('%Value%', event.value);
  toast.add({ severity: 'success', summary: '', detail: messUpdateDept, life: 3000 });
  const payload = {
    assignType: DEPT,
    value: event.value.replace(' ', '_'),
    email: data.coEmail
  };
  await Promise.all([quoteAssignStore.handleAssign(payload, data.quoteID), updateDeptSts(NEW, data)]);
};

const handleView = async (quote) => {
  viewQuoteStore.getAction('view');
  isLoading.value = true;
  viewQuoteStore
    .viewPDF(quote?.quoteID)
    .then(() => {
      dialog.open(ViewPDFModal, {
        props: {
          header: t('pdf.view_pdf'),
          modal: true,
          style: { width: '90%' }
        }
      });
    })
    .catch(() => {
      toast.add({ severity: 'info', summary: '', detail: t('my_quote.toastMessage.pdfNotGenerated'), life: 3000 });
    })
    .finally(() => {
      isLoading.value = false;
    });
};

const handleEdoc = (id) => {
  const payload = {
    id: id,
    type: 'E_DOC'
  };
  viewQuoteStore.handleViewUrlQuote(payload);
  dialog.open(UrlQuoteModal, {
    props: {
      header: t('view.edoc_view'),
      style: {
        width: '95vw'
      },
      breakpoints: {
        '960px': '75vw',
        '640px': '90vw'
      },
      modal: true
    }
  });
};

const handleInvoice = (id) => {
  const payload = {
    id: id,
    type: 'INVOICE'
  };
  viewQuoteStore.handleViewUrlQuote(payload);
  dialog.open(UrlQuoteModal, {
    props: {
      header: t('view.invoice_view'),
      style: {
        width: '95vw'
      },
      breakpoints: {
        '960px': '75vw',
        '640px': '90vw'
      },
      modal: true
    }
  });
};

const handlePayment = (quote: any) => {
  const quoteIdArray = quote?.bookNumber?.split(',');
  const dialogRef = dialog.open(Payment, {
    props: {
      header: t('my_quote.payment_for') + quoteIdArray[0],
      modal: true
    },
    data: {
      quote
    },
    emits: {
      isSave: (value) => {
        if (value === true) {
          dialogRef.close();
          getListQuote();
        }
      }
    }
  });
};

const filterStatus = (bookingId, status) => {
  if (!bookingId) {
    return cloneDeep(quoteSts.value).filter((item) => {
      if (status !== RQ_TO_BOOK) {
        item.disabled = false;
      }
      return item.before_book;
    });
  }
  return cloneDeep(quoteSts.value);
};

const handleViewRule = (qId: string, quoteType: string) => {
  if (quoteType === PACKAGE) {
    handlePackageViewRule(qId, quoteType);
  } else {
    handleTourViewRule(qId, quoteType);
  }
};

const getUserNameByEmail = (role, email, users) => {
  const listMember = getDepartment(role, users)?.members;
  if (listMember?.length > 0) {
    return listMember?.find((user) => user.email === email)?.label;
  } else {
    return '';
  }
};

const handlePackageViewRule = (qId: string, quoteType: string) => {
  viewRuleStore.getLogRule(qId).then((res) => {
    dialog.open(ViewRuleModal, {
      props: {
        header: `${t('my_quote.rule_view')} (${qId})`,
        style: {
          width: '95vw'
        },
        breakpoints: {
          '960px': '75vw',
          '640px': '90vw'
        },
        modal: true,
        maximizable: true
      },
      data: {
        ruleValue: res,
        quoteType: quoteType
      }
    });
  });
};

const handleTourViewRule = (qId: string, quoteType: string) => {
  viewRuleStore.getTourLogRule(qId).then((res) => {
    isNotExisted.value = res.rule?.some((rules: any) => {
      const checkExisted = rules.tourLog.some((tour: any) => {
        return !tour.isExisted;
      });
      return checkExisted;
    });

    if (isNotExisted.value) {
      openNoticeNotFoundRuleTourInfo(res.rule);
    } else {
      dialog.open(TourViewRuleModal, {
        props: {
          header: `${t('my_quote.rule_view')} (${qId})`,
          style: {
            width: '95vw'
          },
          breakpoints: {
            '960px': '75vw',
            '640px': '90vw'
          },
          modal: true,
          maximizable: true
        },
        data: {
          ruleValue: res,
          quoteType: quoteType
        }
      });
    }
  });
};

const converArrayToString = (data, separator) => {
  let result = '';
  if (typeof data === 'string') {
    result = data.split(',');
  } else {
    result = data;
  }

  if (result?.length === 1) {
    return separator + ' ' + result[0];
  }
  let stringData = '';
  result?.forEach((item) => {
    stringData += `${separator} ${item} \n`;
  });
  return stringData;
};

const openNoticeNotFoundRuleTourInfo = (data: any) => {
  const dialogRef = dialog.open(NoticeNotFoundRuleTourInfo, {
    props: {
      header: t('management.tour.confirm.not_found'),
      modal: true
    },
    data: {
      dataWrong: data
    },
    emits: {
      isClose: () => {
        dialogRef.close();
      }
    }
  });
};

const renderLanguage = (lang) => {
  return lang === LANG_FRENCH ? t('promos.labels.french') : t('promos.labels.english');
};

const filterQuote = useDebounceFn(() => {
  filters.value['global'].value = filterGlobalField.value;
}, 400);

const updateLangQuote = async (data, event) => {
  await adminQuoteStore
    .updateLangQuote(data.quoteID, event)
    .then(() => {
      toast.add({ severity: 'success', summary: '', detail: t('notification.update_success'), life: 3000 });
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.update_error'), life: 5000 });
    });
};

const formatBookingId = (bookingIds: string) => {
  if (bookingIds) {
    return bookingIds.replace(/,/g, '\n');
  }
};

const checkLengthSeemore = (bookingIds: string) => {
  if (bookingIds) {
    return formatBookingId(bookingIds)?.match(/\n/g)?.length > 2;
  }
};

const openSeeMore = (dataField: string, header: string) => {
  const dialogRef = dialog.open(SeeMoreToursDescription, {
    props: {
      header: `${header}`,
      modal: true,
      style: {
        width: '20vw'
      }
    },
    data: {
      dataField: dataField
    },
    emits: {
      onClose: () => {
        dialogRef.close();
      }
    }
  });
};

const convertString = (quoteIds: any) => {
  return quoteIds?.join(', ');
};

const filterQuoteAfterInterval = () => {
  if (filterTotalData.value) {
    handleFilterTotal(filterTotalData.value);
  }
};

const canEditStatus = GQT_COORDINATOR !== currentUser.value?.role && authorizations?.quote_management?.change_quote_status_edit;
airManagementStore.getListAir();

let interval: ReturnType<typeof setInterval>;
function initiateQuoteRefresh() {
  getListQuote();
  if (REFRESH_RATE && REFRESH_RATE > 0) {
    try {
      interval = setInterval(() => {
        isReloading.value = true;
        getListQuote(false).then(() => {
          isReloading.value = false;
          filterQuoteAfterInterval();
        });
      }, REFRESH_RATE);
    } finally {
      isReloading.value = false;
    }
  }
}

// Temporary fix due to issue in PrimeVue 4.2.5 for frozen columns
// https://github.com/primefaces/primevue/issues/6067
// To be removed as soon as we have a fix
const frozenTableQuote = () => {
  const tableQuote = dataTableQuoteRef.value?.$el.querySelector('.p-datatable-thead');
  if (tableQuote) {
    const colWidths = tableQuote.querySelectorAll('th');
    document.documentElement.style.setProperty('--quote-width-1', 0 + 'px');
    document.documentElement.style.setProperty('--quote-width-2', 0.5 + +colWidths[0].offsetWidth + 'px');
    document.documentElement.style.setProperty('--quote-width-3', +colWidths[0].offsetWidth + +colWidths[1].offsetWidth + 0.5 + 'px');
  }
};

onMounted(async () => {
  frozenTableQuote();
  window.addEventListener('resize', frozenTableQuote);
  currentUser.value = await userInfo.getUserInfo();
  quotes.value = [];
  await getGroupTypeData();
  await Promise.all([myQuoteStore.getReminders({ email: currentUser.value?.email })]);

  layoutState.staticMenuDesktopInactive.value = true;
  await userStore.handleGetUserAssign();
  if (GQT_COORDINATOR === currentUser.value?.role) {
    filters.value.assignedTo.constraints[0].value = getUserNameByEmail(currentUser.value?.role, currentUser.value?.email, listUser.value);
  } else {
    handleFilterByRole(currentUser.value?.role ?? '');
  }
  initiateQuoteRefresh();
  await adminQuoteStore.lastVisitStatic();
});

onUnmounted(() => {
  clearInterval(interval);
  window.removeEventListener('resize', frozenTableQuote);
});

const handleFilterTotal = async (event: any) => {
  if (!filterTotalData.value || filterTotalData.value.key !== event.key) {
    filterTotalData.value = event;
    isLoading.value = true;
    await getListQuote();
    isLoading.value = false;
  }
  quotes.value = quotes.value.filter((quote: any) => event.quoteIds.includes(quote.quoteID));
};

const applyNewValue = (event: any) => {
  if (filterTotalData.value) {
    filterTotalData.value = event[filterTotalData.value.key - 1];
  }
};
</script>

<template>
  <div class="quote-management">
    <LoadingSpinner v-if="isLoading || doLoading" />
    <AdminStats :deptChangedName="deptChangedName" @staticEvent="staticEvent($event)" />
    <div class="grid" v-if="authorizations?.quote_management?.reminder_view">
      <div class="col-12">
        <div class="card">
          <h2>{{ $t('task_list.reminders') }}</h2>
          <TaskList :email="currentUser?.email" />
        </div>
      </div>
    </div>
    <div class="grid">
      <div class="col-12">
        <div class="card">
          <div class="mb-5 flex justify-content-between flex-col sm:flex-row">
            <div class="mr-3">
              <h5>{{ $t('quote_management.quote_queue') }}</h5>
              <p>
                {{ $t('quote_management.title_one') }}
                <br />
                {{ $t('quote_management.title_two') }}
                <br />
                {{ $t('quote_management.title_three') }}
                <br /><br />
                --
                <span v-if="currentLanguage === 'fr'"
                  >{{ $t('my_quote.your') }} {{ $t('my_quote.team') }} {{ $t('common.from').toLowerCase() }}
                  <span class="font-bold">{{ $t('my_quote.group_travel').toLowerCase() }}</span>
                  {{ $t('common.from').toLowerCase() }}
                  <span class="text-primary font-bold">{{ $t('my_quote.acv') + ' ' }} </span>
                </span>
                <span v-else
                  >{{ $t('my_quote.your') }}
                  <span class="text-primary font-bold">{{ $t('my_quote.acv') + ' ' }} </span>
                  <span class="font-bold">{{ $t('my_quote.group_travel') }}</span>
                  {{ $t('my_quote.team') }}</span
                >
              </p>
            </div>
            <div class="white-space-nowrap">
              <h5>{{ $t('quote_management.department') }}</h5>
              <div class="roles">
                <i class="pi pi-users mr-3"></i>
                <b>CO</b> - {{ $t('status.groups_coordinator') }}<br />
                <i class="pi pi-dollar mr-3"></i>
                <b>GC</b> - {{ $t('status.groups_commercial') }}<br />
                <i class="pi pi-chart-line mr-3"></i>
                <b>RM</b> - {{ $t('status.revenua_management') }}<br />
                <i class="pi pi-sitemap mr-3"></i>
                <b>AO</b> - {{ $t('status.group_air') }}<br />
                <i class="pi pi-sitemap mr-3"></i>
                <b>CR</b> - {{ $t('status.group_cruise') }}<br />
                <i class="pi pi-sitemap mr-3"></i>
                <b>IN</b> - {{ $t('status.group_incentives') }}<br />
                <i class="pi pi-cog mr-3"></i>
                <b>SU</b> - {{ $t('status.supervisor') }}
              </div>
            </div>
          </div>
          <div class="filter-bar">
            <FilterTable
              :statisticNoteTotalPublic="statisticNoteTotalPublic"
              :isShowQuoteId="true"
              :isShowArchiveFilter="true"
              :dataFilter="initValueFilter"
              :groupTypeList="groupTypeList"
              @handleFilterData="handleFilterData"
              @handleFilterTotal="handleFilterTotal($event)"
              @applyNewValue="applyNewValue($event)"
            ></FilterTable>
          </div>
          <DataTable
            :value="quotes"
            ref="dataTableQuoteRef"
            :paginator="true"
            class="p-datatable-gridlines w-full p-datatable-sm"
            :rows="pageSizes[0]"
            dataKey="quoteID"
            :rowHover="true"
            v-model:filters="filters"
            v-model:first="startElement"
            :totalRecords="totalRecords"
            filterDisplay="menu"
            responsiveLayout="stack"
            scrollHeight="900px"
            scrollable
            :rowsPerPageOptions="pageSizes"
            :globalFilterFields="[
              'assignedTo',
              'agency',
              'agencyPhone',
              'agent',
              'bookingId',
              'created',
              'depDateOption',
              'dept',
              'deptSts',
              'groupName',
              'groupContact',
              'groupType',
              'groupTypePaxRoom',
              'hotelDestinations',
              'language',
              'quoteID',
              'quoteSts',
              'linkedQuotes'
            ]"
            id="dtManualquote"
          >
            <template #header>
              <div class="flex justify-content-between flex-col sm:flex-row">
                <div class="flex gap-2 align-items-center">
                  <Button type="button" icon="pi pi-filter-slash" :label="$t('common.buttons.clear')" class="p-button-secondary mb-2" @click="clearFilter()" />
                  <div>({{ $t('quote_management.hint_message') }} )</div>
                  <span v-if="isReloading" class="reloader"><img :src="loadingIcon" alt="reloading" /></span>
                </div>
                <div class="flex gap-2 align-items-center">
                  <Button type="button" icon="pi pi-refresh" :label="$t('common.buttons.refresh')" class="p-button-secondary" @click="refreshPage()" />
                  <span class="p-input-icon-left mb-2">
                    <i class="pi pi-search" />
                    <InputText v-model="filterGlobalField" @input="filterQuote" :placeholder="$t('my_quote.search_keyword')" class="w-full" />
                  </span>
                </div>
              </div>
            </template>
            <template #empty> {{ $t('my_quote.no_quotes_found') }} </template>
            <TableColumn :show-filter-operator="false" :show-add-button="false" field="quoteID" class="min-w-8rem" filter frozen>
              <template #header>
                <div>
                  <div>{{ $t('my_quote.quote_id') }} /</div>
                  <div>{{ $t('my_quote.create_date') }} /</div>
                  <div class="white-space-nowrap">{{ $t('my_quote.booking_number') }}</div>
                </div>
              </template>
              <template #body="{ data }">
                <div class="flex w-full justify-content-between">
                  <span class="quote-id-header hidden font-bold">
                    <div>
                      <div>{{ $t('my_quote.quote_id') }} /</div>
                      <div>{{ $t('my_quote.create_date') }} /</div>
                      <div class="white-space-nowrap">{{ $t('my_quote.booking_number') }}</div>
                    </div>
                  </span>
                  <div>
                    <p class="mb-2">{{ data.quoteID }}</p>
                    <div class="linked-quotes mb-2">
                      <strong class="text-sm">{{ $t('worksheet.linked_to') }}:</strong>
                      <div class="mb-1 text-elipsis font-normal">{{ formatBookingId(data.linkedQuotes) || '-' }}</div>
                      <span v-if="checkLengthSeemore(data.linkedQuotes)" class="view-more">
                        <a class="text-blue-600 font-bold text-sm underline cursor-pointer text-left uppercase" @click="openSeeMore(formatBookingId(data.linkedQuotes), $t('worksheet.linked_quotes'))">{{ $t('common.labels.see_more') }}</a>
                      </span>
                    </div>
                    <div class="mb-2">
                      <strong>{{ $t('my_quote.created') }}</strong>
                      <p class="mb-0">{{ formatDate(data.created, 'dd-LLL-yyyy', undefined, currentLanguage) }}</p>
                      <p>{{ formatDate(data.created, 'HH:mm:ss') }}</p>
                    </div>
                    <div>
                      <strong class="text-sm">{{ $t('my_quote.booking_number') }}:</strong>
                      <p class="mb-0 text-primary text-elipsis">{{ formatBookingId(data.bookingId) || '-' }}</p>
                      <span v-if="checkLengthSeemore(data.bookingId)" class="view-more">
                        <a class="text-blue-600 font-bold text-sm underline cursor-pointer text-left uppercase" @click="openSeeMore(formatBookingId(data.bookingId), $t('my_quote.booking_number'))">{{ $t('common.labels.see_more') }}</a>
                      </span>
                    </div>
                  </div>
                </div>
              </template>
              <template #filter="{ filterModel }">
                <InputText type="text" v-model="filterModel.value" class="p-column-filter" :placeholder="$t('my_quote.quote_id')" />
              </template>
              <template #filterclear="{ filterCallback }">
                <Button type="button" icon="pi pi-times" @click="handleClearFilter(filterCallback)" class="p-button-secondary"></Button>
              </template>
              <template #filterapply="{ filterCallback }">
                <Button type="button" icon="pi pi-check" @click="handleFilterApply(filterCallback)" class="p-button-success"></Button>
              </template>
            </TableColumn>
            <TableColumn :show-filter-operator="false" :show-add-button="false" field="language" :header="$t('voucher.tableHeader.lang')" filter class="break-word min-w-8rem" frozen>
              <template #body="{ data }">
                <div>
                  <Select class="table-select w-full hidden" :options="langQuote" optionLabel="label" optionValue="value" v-model="data.language" @change="updateLangQuote(data, $event.value)">
                    <template #option="slotProps">
                      <div class="font-bold">{{ slotProps.option?.label }}</div>
                    </template>
                  </Select>
                  <div class="font-bold">
                    {{ data.language }}
                  </div>
                </div>
              </template>
              <template #filter="{ filterModel }">
                <Select v-model="filterModel.value" :options="langQuote" optionLabel="label" optionValue="value" :placeholder="$t('voucher.tableHeader.lang')" class="p-column-filter" :showClear="true">
                  <template #option="slotProps">
                    <option class="font-bold">{{ slotProps.option.label }}</option>
                  </template>
                </Select>
              </template>
              <template #filterclear="{ filterCallback }">
                <Button type="button" icon="pi pi-times" @click="handleClearFilter(filterCallback)" class="p-button-secondary"></Button>
              </template>
              <template #filterapply="{ filterCallback }">
                <Button type="button" icon="pi pi-check" @click="handleFilterApply(filterCallback)" class="p-button-success"></Button>
              </template>
            </TableColumn>
            <TableColumn :show-filter-operator="false" :show-add-button="false" v-if="authorizations?.quote_management?.change_quote_status_view" field="quoteSts" :header="$t('my_quote.quote_status')" class="min-w-10rem" filter frozen>
              <template #body="{ data }">
                <div>
                  <Select
                    class="table-select"
                    :options="filterStatus(data.bookingId, data.quoteSts)"
                    :disabled="!((currentUser?.role === GQT_COORDINATOR && currentUser?.email === data?.coEmail) || canEditStatus)"
                    optionDisabled="disabled"
                    optionLabel="label"
                    optionValue="value"
                    v-model="data.quoteSts"
                    @change="updateQuoteSts(data, $event.value)"
                  >
                    <template #value="slotProps">
                      <div v-if="slotProps.value">
                        <div class="overflow-hidden text-overflow-ellipsis">{{ quoteSts.find((status) => status.value === slotProps.value)?.label }}</div>
                      </div>
                    </template>
                  </Select>

                  <div>
                    <div class="text-base mt-4" v-if="data.quoteSts === DEPOSIT_DUE">
                      <div class="mb-1">
                        <strong>{{ $t('common.date') }}: </strong> {{ formatDate(data.depositDate, 'dd-LLL-yyyy', false, currentLanguage) }}
                      </div>
                      <div>
                        <strong>{{ $t('common.amount') }}: </strong> {{ formatCurrency(data.depositAmount) }}
                      </div>
                    </div>
                    <div class="text-base mt-4" v-if="data.quoteSts === FINAL_PAYMENT_DUE">
                      <div class="mb-1">
                        <strong>{{ $t('common.date') }}: </strong> {{ formatDate(data.finalPaymentDate, 'dd-LLL-yyyy', false, currentLanguage) }}
                      </div>
                      <div>
                        <strong>{{ $t('common.amount') }}: </strong> {{ formatCurrency(data.finalPayment) }}
                      </div>
                    </div>
                    <div class="text-base mt-4" v-if="data.quoteSts === COMPLETE_NAMES">
                      <div class="mb-1">
                        <strong>{{ $t('common.date') }}: </strong> {{ formatDate(data.passengerNameDueDate, 'dd-LLL-yyyy', false, currentLanguage) }}
                      </div>
                    </div>
                  </div>
                </div>
              </template>
              <template #filter="{ filterModel }">
                <Select v-model="filterModel.value" :options="quoteSts" optionLabel="label" optionValue="value" :placeholder="$t('common.any')" class="p-column-filter w-14rem" :showClear="true">
                  <template #option="slotProps">
                    <option>{{ slotProps.option.label }}</option>
                  </template>
                </Select>
              </template>
              <template #filterclear="{ filterCallback }">
                <Button type="button" icon="pi pi-times" @click="handleClearFilter(filterCallback)" class="p-button-secondary"></Button>
              </template>
              <template #filterapply="{ filterCallback }">
                <Button type="button" icon="pi pi-check" @click="handleFilterApply(filterCallback)" class="p-button-success"></Button>
              </template>
            </TableColumn>
            <TableColumn :show-filter-operator="false" :show-add-button="false" v-if="authorizations?.quote_management?.change_quote_assign_view" field="assignedTo" :header="$t('my_quote.quote_assigned_to')" class="min-w-8rem">
              <template #body="{ data }">
                <Select
                  class="table-select"
                  :disabled="currentUser?.role !== GQT_ADMIN && currentUser?.role !== GQT_SUPERVISOR && !authorizations?.quote_management?.change_quote_assign_edit"
                  :options="[createUnassignedStatus(), getDepartment(GQT_COORDINATOR, listUser), getDepartment(GQT_AIR, listUser), getDepartment(GQT_CRUISE, listUser), getDepartment(GQT_INCENTIVE, listUser), getDepartment(GQT_SUPERVISOR, listUser)]"
                  optionLabel="label"
                  optionValue="label"
                  optionGroupChildren="members"
                  optionGroupLabel="displayName"
                  :placeholder="$t('status.unassigned')"
                  v-model="data.assignedTo"
                  @change="
                    handleAssignTo($event, data, [
                      ...getUsersByDept(GQT_COORDINATOR, listUser),
                      ...getUsersByDept(GQT_AIR, listUser),
                      ...getUsersByDept(GQT_CRUISE, listUser),
                      ...getUsersByDept(GQT_INCENTIVE, listUser),
                      ...getUsersByDept(GQT_SUPERVISOR, listUser)
                    ])
                  "
                />
              </template>
              <template #filter="{ filterModel }">
                <InputText type="text" v-model="filterModel.value" class="p-column-filter" :placeholder="$t('common.search_by_assign')" />
              </template>
              <template #filterclear="{ filterCallback }">
                <Button type="button" icon="pi pi-times" @click="handleClearFilter(filterCallback)" class="p-button-secondary"></Button>
              </template>
              <template #filterapply="{ filterCallback }">
                <Button type="button" icon="pi pi-check" @click="handleFilterApply(filterCallback)" class="p-button-success"></Button>
              </template>
            </TableColumn>
            <TableColumn :show-filter-operator="false" :show-add-button="false" field="groupName" :header="$t('my_quote.group_details')" filter class="break-word min-w-8rem">
              <template #body="{ data }">
                <div class="flex flex-col gap-3">
                  <span class="long-text-tooltip font-bold" v-tooltip="data.groupName">
                    {{ data.groupName }}
                  </span>
                  <span class="long-text-tooltip" v-tooltip="data.groupContact">{{ data.groupContact }}</span>
                </div>
              </template>
              <template #filter="{ filterModel }">
                <InputText type="text" v-model="filterModel.value" class="p-column-filter" :placeholder="$t('voucher.tableHeader.groupNamePlaceHolder')" />
              </template>
              <template #filterclear="{ filterCallback }">
                <Button type="button" icon="pi pi-times" @click="handleClearFilter(filterCallback)" class="p-button-secondary"></Button>
              </template>
              <template #filterapply="{ filterCallback }">
                <Button type="button" icon="pi pi-check" @click="handleFilterApply(filterCallback)" class="p-button-success"></Button>
              </template>
            </TableColumn>
            <TableColumn :show-filter-operator="false" :show-add-button="false" field="agencyPhone" :header="$t('my_quote.travel_agent_agency')" class="min-w-10rem" filter>
              <template #body="{ data }">
                <div class="flex flex-col">
                  <span class="font-bold mb-3">{{ data.agencyPhone }}</span>
                  <span class="block font-medium">{{ data.agent }}</span>
                  <span class="block font-medium mb-3">{{ data.email && `(${data.email})` }}</span>
                  <span>{{ data.agency }}</span>
                </div>
              </template>
              <template #filter="{ filterModel }">
                <InputText type="text" v-model="filterModel.value" class="p-column-filter" :placeholder="$t('common.search_by_agency_phone')" />
              </template>
              <template #filterclear="{ filterCallback }">
                <Button type="button" icon="pi pi-times" @click="handleClearFilter(filterCallback)" class="p-button-secondary"></Button>
              </template>
              <template #filterapply="{ filterCallback }">
                <Button type="button" icon="pi pi-check" @click="handleFilterApply(filterCallback)" class="p-button-success"></Button>
              </template>
            </TableColumn>
            <TableColumn :show-filter-operator="false" :show-add-button="false" field="groupType" class="min-w-12rem" filter>
              <template #header>
                <div>
                  <div>{{ $t('advance_quote.form.group_type') }} /</div>
                  <div>{{ $t('common.pax') }} / {{ $t('common.rooms') }}</div>
                </div>
              </template>
              <template #body="{ data }">
                <div class="flex w-full justify-content-between">
                  <span class="quote-id-header hidden font-bold">
                    <div>
                      <div>{{ $t('advance_quote.form.group_type') }} /</div>
                      <div>{{ $t('common.pax') }} / {{ $t('common.rooms') }}</div>
                    </div>
                  </span>
                  <div>
                    <p class="mb-2 font-bold">{{ data.groupType }}</p>
                    <div class="mb-2 long-text-tooltip white-space-pre-line text-sm" v-tooltip.left="{ value: data.groupTypePaxRoom, class: 'white-space-pre-line' }">{{ data.groupTypePaxRoom || '-' }}</div>
                  </div>
                </div>
              </template>
              <template #filter="{ filterModel }">
                <InputText type="text" v-model="filterModel.value" class="p-column-filter" :placeholder="$t('advance_quote.form.group_type')" />
              </template>
              <template #filterclear="{ filterCallback }">
                <Button type="button" icon="pi pi-times" @click="handleClearFilter(filterCallback)" class="p-button-secondary"></Button>
              </template>
              <template #filterapply="{ filterCallback }">
                <Button type="button" icon="pi pi-check" @click="handleFilterApply(filterCallback)" class="p-button-success"></Button>
              </template>
            </TableColumn>
            <TableColumn :show-filter-operator="false" :show-add-button="false" filterField="hotelDestinations" class="min-w-16rem">
              <template #header>
                <div>
                  <div>{{ $t('common.hotel') }} / {{ $t('my_quote.destination') }}</div>
                  <div>{{ $t('common.tour') }} / {{ $t('my_quote.destination') }}</div>
                </div>
              </template>
              <template #body="{ data }">
                <div class="flex w-full justify-content-between">
                  <span class="quote-id-header hidden font-bold">
                    <div>
                      <div>{{ $t('common.hotel') }} /</div>
                      <div>{{ $t('my_quote.destination') }}</div>
                    </div>
                  </span>
                  <div>
                    <p class="long-text-tooltip white-space-pre-line font-bold" v-tooltip.left="{ value: data.hotelDestinations, class: 'white-space-pre-line' }">
                      {{ data.hotelDestinations }}
                    </p>
                  </div>
                </div>
              </template>
              <template #filter="{ filterModel }">
                <InputText type="text" v-model="filterModel.value" class="p-column-filter" :placeholder="$t('management.air.search_by_country')" />
              </template>
              <template #filterclear="{ filterCallback }">
                <Button type="button" icon="pi pi-times" @click="handleClearFilter(filterCallback)" class="p-button-secondary"></Button>
              </template>
              <template #filterapply="{ filterCallback }">
                <Button type="button" icon="pi pi-check" @click="handleFilterApply(filterCallback)" class="p-button-success"></Button>
              </template>
            </TableColumn>
            <TableColumn :show-filter-operator="false" :show-add-button="false" :header="$t('my_quote.departure_date')" filterField="depDateOption" class="min-w-10rem">
              <template #body="{ data }">
                <p class="mb-0 white-space-pre-line font-bold">{{ data.depDateOption }}</p>
              </template>
              <template #filter="{ filterModel }">
                <InputText type="text" v-model="filterModel.value" class="p-column-filter" :placeholder="$t('my_quote.departure_date')" />
              </template>
              <template #filterclear="{ filterCallback }">
                <Button type="button" icon="pi pi-times" @click="handleClearFilter(filterCallback)" class="p-button-secondary"></Button>
              </template>
              <template #filterapply="{ filterCallback }">
                <Button type="button" icon="pi pi-check" @click="handleFilterApply(filterCallback)" class="p-button-success"></Button>
              </template>
            </TableColumn>
            <TableColumn :show-filter-operator="false" :show-add-button="false" :header="$t('my_quote.expires')" filterField="expires" dataType="date" class="min-w-10rem">
              <template #body="{ data }">
                <p class="font-bold">{{ formatDate(data.expires, 'dd-LLL-yyyy', false, currentLanguage) }}</p>
              </template>
              <template #filter="{ filterModel }">
                <DatePicker v-model="filterModel.value" dateFormat="dd-M-yy" :placeholder="$t('common.date_format')">
                  <template #footer>
                    <p class="text-center">
                      <small>{{ $t('common.press_space') }}</small>
                    </p>
                  </template>
                </DatePicker>
              </template>
              <template #filterclear="{ filterCallback }">
                <Button type="button" icon="pi pi-times" @click="handleClearFilter(filterCallback)" class="p-button-secondary"></Button>
              </template>
              <template #filterapply="{ filterCallback }">
                <Button type="button" icon="pi pi-check" @click="handleFilterApply(filterCallback)" class="p-button-success"></Button>
              </template>
            </TableColumn>
            <TableColumn :show-filter-operator="false" :show-add-button="false" v-if="authorizations?.quote_management?.change_dept_assign_view" field="dept" :header="$t('my_quote.dept')" class="overflow-hidden w-6rem" filter>
              <template #body="{ data }">
                <Select class="table-select" :disabled="!authorizations?.quote_management?.change_dept_assign_edit" :options="departments" optionLabel="standsFor" optionValue="value" v-model="data.dept" @change="updateDept($event, data)">
                  <template #option="slotProps">
                    {{ slotProps.option.label }}
                  </template>
                </Select>
              </template>
              <template #filter="{ filterModel }">
                <Select v-model="filterModel.value" :options="departs" :placeholder="$t('common.any')" class="p-column-filter" :showClear="true">
                  <template #value="slotProps">
                    <span :class="'customer-badge depart-' + slotProps.value" v-if="slotProps.value">{{ slotProps.value }}</span>
                    <span v-else>{{ slotProps.placeholder }}</span>
                  </template>
                  <template #option="slotProps">
                    <span :class="'customer-badge depart-' + slotProps.option">{{ slotProps.option }}</span>
                  </template>
                </Select>
              </template>
              <template #filterclear="{ filterCallback }">
                <Button type="button" icon="pi pi-times" @click="handleClearFilter(filterCallback)" class="p-button-secondary"></Button>
              </template>
              <template #filterapply="{ filterCallback }">
                <Button type="button" icon="pi pi-check" @click="handleFilterApply(filterCallback)" class="p-button-success"></Button>
              </template>
            </TableColumn>
            <TableColumn
              :show-filter-operator="false"
              :show-add-button="false"
              v-if="authorizations?.quote_management?.dept_status_view || authorizations?.quote_management?.dept_assign_view"
              field="deptSts"
              :header="$t('my_quote.dept_status_assigned')"
              class="w-10rem"
              filter
            >
              <template #body="{ data }">
                <div class="flex flex-col gap-2">
                  <Select
                    class="table-select"
                    v-if="authorizations?.quote_management?.dept_status_view"
                    :disabled="!authorizations?.quote_management?.dept_assign_edit || (currentUser?.role !== GQT_ADMIN && currentUser?.role !== GQT_SUPERVISOR && MAP_DEPARTMENT_TO_ROLE[data.dept] !== currentUser?.role)"
                    :options="deptSts"
                    optionLabel="label"
                    optionValue="value"
                    v-model="data.deptSts"
                    @change="updateDeptSts($event, data)"
                  >
                    <template #option="slotProps">
                      {{ slotProps.option.label }}
                    </template>
                  </Select>
                  <Select
                    v-if="authorizations?.quote_management?.dept_assign_view"
                    class="table-select"
                    :disabled="!authorizations?.quote_management?.dept_assign_edit || (currentUser?.role !== GQT_ADMIN && currentUser?.role !== GQT_SUPERVISOR && MAP_DEPARTMENT_TO_ROLE[data.dept] !== currentUser?.role)"
                    :options="getUsersByDept(MAP_DEPARTMENT_TO_ROLE[data.dept], listUser)"
                    :placeholder="$t('status.unassigned')"
                    optionLabel="label"
                    optionValue="label"
                    v-model="data.assignee"
                    @change="updateAssignee($event, data, [...getUsersByDept(MAP_DEPARTMENT_TO_ROLE[data.dept], listUser)])"
                  />
                </div>
              </template>
              <template #filter="{ filterModel }">
                <Select v-model="filterModel.value" :options="deptSts" optionLabel="label" optionValue="value" :placeholder="$t('common.any')" class="p-column-filter w-12rem" :showClear="true" />
              </template>
              <template #filterclear="{ filterCallback }">
                <Button type="button" icon="pi pi-times" @click="handleClearFilter(filterCallback)" class="p-button-secondary"></Button>
              </template>
              <template #filterapply="{ filterCallback }">
                <Button type="button" icon="pi pi-check" @click="handleFilterApply(filterCallback)" class="p-button-success"></Button>
              </template>
            </TableColumn>
            <TableColumn :header="$t('my_quote.actions')" frozen alignFrozen="right" class="text-center action-column mx-auto">
              <template #body="{ data }">
                <div class="flex flex-wrap w-full">
                  <div class="flex basis p-1">
                    <Button @click="handleView(data)" :label="$t('my_quote.view')" class="p-button-outlined p-button-xs border-noround w-full" />
                  </div>
                  <div class="flex basis p-1" v-if="authorizations?.quote_management?.worksheet_view">
                    <Button :label="$t('my_quote.worksheet')" class="p-button-outlined p-button-xs border-noround w-full" @click="openWorksheetModal(data.quoteType, data.quoteID, data?.language)" />
                  </div>
                  <div class="flex basis p-1">
                    <Button :label="$t('my_quote.tts_bkg_no')" class="p-button-outlined p-button-xs border-noround w-full" @click="openBookingModal(data.quoteID)" />
                  </div>
                  <div class="flex basis p-1" v-if="data.coEmail === currentUser?.email || authorizations?.quote_management?.change_to_completed_view">
                    <Button :label="$t('my_quote.update_pdf')" class="p-button-outlined p-button-xs border-noround w-full" @click="openModalUpdatePDF(data)" />
                  </div>
                  <div class="flex basis p-1" v-if="(data.coEmail === currentUser?.email || currentUser?.role === GQT_ADMIN || currentUser?.role === GQT_SUPERVISOR) && priorityStatus.get(data.quoteSts) >= statusPriorityNumber.FIVE">
                    <Button :label="$t('my_quote.payment')" class="p-button-outlined p-button-xs border-noround w-full" @click="handlePayment(data)" />
                  </div>
                  <div class="flex basis p-1" v-if="(data.coEmail === currentUser?.email || currentUser?.role === GQT_ADMIN || currentUser?.role === GQT_SUPERVISOR) && priorityStatus.get(data.quoteSts) >= statusPriorityNumber.FIVE">
                    <Button :label="$t('my_quote.e_doc')" :disabled="!data.bookingId" class="p-button-outlined p-button-xs border-noround w-full" @click="handleEdoc(data.bookingId)" />
                  </div>
                  <div class="flex basis p-1" v-if="(data.coEmail === currentUser?.email || currentUser?.role === GQT_ADMIN || currentUser?.role === GQT_SUPERVISOR) && priorityStatus.get(data.quoteSts) >= statusPriorityNumber.FIVE">
                    <Button :label="$t('my_quote.invoice')" :disabled="!data.bookingId" class="p-button-outlined p-button-xs border-noround w-full" @click="handleInvoice(data.bookingId)" />
                  </div>
                  <div v-if="authorizations?.quote_management?.note_view" class="flex basis p-1">
                    <Button :id="data.quoteID" class="p-button-outlined p-button-xs border-noround w-full" @click="openExtNotes(data.quoteID, data.hasNoteToRead, data)">
                      <span class="p-button-label">{{ $t('my_quote.notes') }}</span>
                      <span v-if="data.hasNoteToRead">
                        <i class="pi pi-exclamation-circle text-red-500"></i>
                      </span>
                    </Button>
                  </div>
                  <div
                    class="flex basis p-1"
                    v-if="(data.coEmail === currentUser?.email && (data.quoteType === PACKAGE || data.quoteType === TOUR)) || (authorizations?.quote_management?.rule_quote_view && (data.quoteType === PACKAGE || data.quoteType === TOUR))"
                  >
                    <Button :label="$t('my_quote.rules')" class="p-button-outlined p-button-xs border-noround w-full" @click="handleViewRule(data.quoteID, data.quoteType)" />
                  </div>
                </div>
              </template>
            </TableColumn>
          </DataTable>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
:deep() {
  td {
    .p-select .p-select-label {
      font-size: 1.225rem;
    }

    .p-select .p-inputtext {
      padding: 0.25rem 0.5rem;
    }
  }

  .p-select-items .p-select-item {
    padding: 0.75rem 1rem !important;
    font-size: 0.875rem;
  }

  .p-frozen-column {
    background: #f8f9fa;
    z-index: 1;
  }
  .p-datatable-thead {
    z-index: 2;
  }

  .p-datatable table {
    border-collapse: separate;

    .p-column-header-content {
      text-align: center;
      justify-content: center;
    }
  }
}

::v-deep(.p-datatable-scrollable .p-frozen-column) {
  font-weight: bold;
}

::v-deep(.p-splitbutton.p-button-xs) {
  font-size: 1.05rem;
  padding: 0;
}

::v-deep(.p-button-xs) {
  font-size: 1.05rem;
  padding: 0.35rem 0.7rem;
}

::v-deep(.p-button-xs button) {
  font-size: 1.05rem;
  padding: 0.35rem 0.7rem;
  border-radius: 0;
}

::v-deep(.p-datatable-table) {
  width: 100%;
  tr {
    th,
    td {
      padding: 0.25rem !important;
    }
  }
  .table-select {
    max-width: 14.4rem;
  }
}
</style>
